import { useRef, useState, useEffect, useCallback } from "react";
import { ZoomIn, RotateCw, Palette } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const V = 256; // viewport size (px)
const OUT = 512; // exported image size (px)

const FILTERS = [
  { key: "none", label: "Original", css: "" },
  { key: "bw", label: "B&W", css: "grayscale(1)" },
  { key: "warm", label: "Warm", css: "sepia(0.4) saturate(1.5) brightness(1.02)" },
  { key: "cool", label: "Cool", css: "saturate(0.8) brightness(1.06) contrast(1.05)" },
  { key: "vivid", label: "Vivid", css: "saturate(1.6) contrast(1.12) brightness(1.02)" },
  { key: "vintage", label: "Vintage", css: "sepia(0.55) contrast(0.94) brightness(1.05) saturate(1.15)" },
  { key: "soft", label: "Soft", css: "contrast(0.9) brightness(1.07) saturate(0.95) blur(0.4px)" },
];

// Square avatar cropper — drag to reposition, slider to zoom, button to rotate 90°.
// Rotation is applied by pre-rotating the source into a working image, so the pan/zoom/crop math
// stays simple. Always covers the viewport; exports a center-square JPEG data URI at OUTxOUT.
export function ImageCropper({ src, onCancel, onCropped }) {
  const [rot, setRot] = useState(0);
  const [filter, setFilter] = useState("none");
  const [workSrc, setWorkSrc] = useState(src);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [nat, setNat] = useState(null);
  const dragRef = useRef(null);

  const cssFilter = FILTERS.find((f) => f.key === filter)?.css || "";

  useEffect(() => { setRot(0); setFilter("none"); }, [src]); // new image → reset rotation & filter

  // Produce the working image = source rotated by `rot`.
  useEffect(() => {
    if (!src) return;
    if (rot === 0) { setWorkSrc(src); return; }
    const im = new window.Image();
    im.onload = () => {
      const swap = rot === 90 || rot === 270;
      const cw = swap ? im.naturalHeight : im.naturalWidth;
      const ch = swap ? im.naturalWidth : im.naturalHeight;
      const c = document.createElement("canvas");
      c.width = cw;
      c.height = ch;
      const ctx = c.getContext("2d");
      ctx.translate(cw / 2, ch / 2);
      ctx.rotate((rot * Math.PI) / 180);
      ctx.drawImage(im, -im.naturalWidth / 2, -im.naturalHeight / 2);
      setWorkSrc(c.toDataURL("image/jpeg", 0.92));
    };
    im.src = src;
  }, [src, rot]);

  // Load working image → reset zoom/pan to fit.
  useEffect(() => {
    if (!workSrc) return;
    const im = new window.Image();
    im.onload = () => { setNat({ w: im.naturalWidth, h: im.naturalHeight }); setZoom(1); setPan({ x: 0, y: 0 }); };
    im.src = workSrc;
  }, [workSrc]);

  const baseScale = nat ? V / Math.min(nat.w, nat.h) : 1;
  const eff = baseScale * zoom;
  const dispW = nat ? nat.w * eff : V;
  const dispH = nat ? nat.h * eff : V;
  const maxX = Math.max(0, (dispW - V) / 2);
  const maxY = Math.max(0, (dispH - V) / 2);

  const clamp = useCallback(
    (p) => ({ x: Math.max(-maxX, Math.min(maxX, p.x)), y: Math.max(-maxY, Math.min(maxY, p.y)) }),
    [maxX, maxY],
  );

  useEffect(() => { setPan((p) => clamp(p)); }, [zoom, nat, clamp]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pan.x, oy: pan.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setPan(clamp({ x: dragRef.current.ox + (e.clientX - dragRef.current.sx), y: dragRef.current.oy + (e.clientY - dragRef.current.sy) }));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const imgLeft = V / 2 - dispW / 2 + pan.x;
  const imgTop = V / 2 - dispH / 2 + pan.y;

  const apply = () => {
    if (!nat) return;
    const srcX = (0 - imgLeft) / eff;
    const srcY = (0 - imgTop) / eff;
    const srcSize = V / eff;
    const im = new window.Image();
    im.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = OUT;
      canvas.height = OUT;
      const ctx = canvas.getContext("2d");
      if (cssFilter) ctx.filter = cssFilter;
      ctx.drawImage(im, srcX, srcY, srcSize, srcSize, 0, 0, OUT, OUT);
      onCropped(canvas.toDataURL("image/jpeg", 0.85));
    };
    im.src = workSrc;
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid="image-cropper">
      <div
        className="relative touch-none cursor-grab overflow-hidden rounded-2xl bg-black active:cursor-grabbing"
        style={{ width: V, height: V }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        data-testid="image-cropper-viewport"
      >
        {workSrc && (
          <img
            src={workSrc}
            alt="Crop preview"
            draggable={false}
            className="select-none"
            style={{ position: "absolute", left: imgLeft, top: imgTop, width: dispW, height: dispH, maxWidth: "none", filter: cssFilter || "none" }}
          />
        )}
        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/25" />
      </div>
      <div className="flex w-full items-center gap-3">
        <ZoomIn className="h-4 w-4 shrink-0 text-bloq-muted" />
        <Slider value={[zoom]} min={1} max={3} step={0.01} onValueChange={(v) => setZoom(v[0])} data-testid="image-cropper-zoom" />
        <button
          type="button"
          onClick={() => setRot((r) => (r + 90) % 360)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/12 text-bloq-muted transition-colors hover:text-white"
          title="Rotate 90°"
          data-testid="image-cropper-rotate"
        >
          <RotateCw className="h-4 w-4" />
        </button>
      </div>
      <div className="flex w-full items-center gap-2">
        <Palette className="h-4 w-4 shrink-0 text-bloq-muted" />
        <div className="flex flex-1 gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                "shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors",
                filter === f.key ? "border-bloq-mint/50 bg-bloq-mint/15 text-bloq-mint" : "border-white/10 text-bloq-muted hover:text-white",
              )}
              data-testid={`image-cropper-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex w-full gap-2">
        <Button variant="ghost" onClick={onCancel} className="flex-1 rounded-full border border-white/12" data-testid="cropper-cancel-btn">
          Cancel
        </Button>
        <Button onClick={apply} disabled={!nat} className="flex-1 rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="cropper-apply-btn">
          Apply
        </Button>
      </div>
    </div>
  );
}
